import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  Close as CloseIcon,
  DeleteOutline,
  EditOutlined,
  ExpandMore,
  FileDownloadOutlined,
  Inventory2Outlined,
  TravelExploreOutlined,
  VisibilityOutlined,
} from '@mui/icons-material'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import axiosInstant from '../../api'
import {
  rmnCategoriesEndpoint,
  rmnCategoryCrawlEndpoint,
  rmnCategoryEndpoint,
  rmnImportCategoryUrlEndpoint,
} from '../../api/endpoints'
import { IconPlus, IconRefresh } from '../../utils/icons'
import { RunningBadge } from '../../components/RunningBadge'
import { MainContainer } from '../../components/ContentWrapper'
import { ConfirmationDialog } from '../../components/ConfirmationDialog/ConfirmationDialog'
import { ListPageHeader } from '../../components/ListPageHeader/ListPageHeader'
import { DotLottleLoader } from '../../components/Loader'
import { CategoryAuditForm } from '../../components/PdpAudit/CategoryAuditForm'
import { ReportViewerDialog } from '../../components/PdpAudit/ReportViewerDialog'
import { ReportHistorySection } from '../../components/PdpAudit/ReportHistorySection'
import { downloadRmnReportById } from '../../components/PdpAudit/reportFiles'
import { RmnCategoryNode } from '../../types/rmn'
import { useNetworkScope } from '../../hooks/useNetworkScope'
import { EditUrlDialog } from './EditUrlDialog'
import { ScoreChip, isDisplayableUrl, viewsDiscoveredProducts } from './catalogShared'
import {
  RmnCrawlRow,
  crawlFailureMessage,
  crawlRowLabel,
  crawlStatusLabel,
  isBrandKindCrawl,
  isCrawlInProgress,
  isSiteCrawl,
  isCrawlStoppable,
  requestCrawlStop,
  useRmnCrawls,
} from './useRmnCrawls'

// Catalog › Categories (TDC-1499): the brand's crawled category tree
// (GET /rmn/categories — network-scoped, live discovered-product counts and
// average PDP scores). Each row can launch the category rollup report;
// finished reports live in this page's own Category Reports section below
// the tree (the PDP Audit page is retired). Row URLs are editable — mainly
// for manual categories, whose placeholder url isn't displayable. Brands
// that haven't been crawled yet see the empty state — the crawler runs for
// RMN networks (admin-managed today).

const SITE_CRAWL_MESSAGE =
  'A full catalog analysis is running — try again when it finishes.'

const crawlButtonTitle = (
  crawlable: boolean,
  rowActive: boolean,
  siteCrawlActive: boolean,
): string => {
  if (!crawlable) return 'Set a category URL we can analyze first (edit the URL).'
  if (rowActive) return 'This category is being analyzed — new pages appear when it finishes.'
  if (siteCrawlActive) return SITE_CRAWL_MESSAGE
  return 'Analyze this category again — picks up missed subcategories and new products.'
}

interface RowProps {
  node: RmnCategoryNode
  expanded: Set<number>
  // Categories with a report currently generating — their Run report
  // buttons disable until the reports section's poll sees them finish.
  runningIds: Set<number>
  // Categories with their own analysis in flight — just their Analyze
  // button disables (one analysis per target; other rows stay available).
  activeCategoryIds: Set<number>
  // An admin full-site analysis pauses every Analyze button.
  siteCrawlActive: boolean
  onToggle: (id: number) => void
  onCrawl: (node: RmnCategoryNode) => void
  onViewProducts: (node: RmnCategoryNode) => void
  onRunReport: (node: RmnCategoryNode) => void
  onViewReport: (node: RmnCategoryNode) => void
  onEditUrl: (node: RmnCategoryNode) => void
  onDelete: (node: RmnCategoryNode) => void
}

function CategoryRow({
  node,
  expanded,
  runningIds,
  activeCategoryIds,
  siteCrawlActive,
  onToggle,
  onCrawl,
  onViewProducts,
  onRunReport,
  onViewReport,
  onEditUrl,
  onDelete,
}: RowProps) {
  const hasChildren = !!node.children && node.children.length > 0
  const isOpen = expanded.has(node.id)
  const [downloading, setDownloading] = useState(false)
  const handleDownloadReport = async () => {
    if (!node.latest_report || downloading) return
    setDownloading(true)
    try {
      await downloadRmnReportById(node.latest_report.id)
    } finally {
      setDownloading(false)
    }
  }
  // Hierarchical counts (this category plus its whole subtree), disjoint
  // the same way the Brands columns are: catalog = imported products filed
  // here, discovered = analysis rows not yet imported.
  const catalogCount = node.catalog_product_count || 0
  const discoveredCount = node.discovered_product_count || 0
  const scored = node.scored_product_count || 0
  const reportRunning = runningIds.has(node.id)
  const crawlRunning = activeCategoryIds.has(node.id)
  const crawlable = isDisplayableUrl(node.url)

  return (
    <>
      <div
        className="flex items-center gap-2 border-b border-border-token px-3 py-2.5 hover:bg-surface-2"
        style={{ paddingLeft: 12 + (node.depth || 0) * 26 }}
      >
        {hasChildren ? (
          <button
            type="button"
            aria-label={isOpen ? `Collapse ${node.name}` : `Expand ${node.name}`}
            onClick={() => onToggle(node.id)}
            className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
          >
            <ExpandMore
              sx={{
                fontSize: 18,
                transition: 'transform 160ms ease',
                transform: isOpen ? 'none' : 'rotate(-90deg)',
              }}
            />
          </button>
        ) : (
          <span className="w-6 shrink-0" />
        )}
        <span className="flex-1 min-w-0 font-semibold text-[13px] text-text-1 truncate">
          {node.name}
        </span>
        {/* PDP Score: chip + run icon, with the latest READY report's date
            and view/download underneath (same block the Brands and Products
            tables use). */}
        <div className="w-[190px] shrink-0 flex flex-col gap-0.5">
        <div className="flex items-center gap-1.5">
          {typeof node.avg_score === 'number' ? (
            <>
              <ScoreChip
                score={node.avg_score}
                title="Average of the latest scores across this category and its subcategories' products"
              />
              {scored > 0 && (
                <span className="text-[11px] text-muted">{scored} scored</span>
              )}
            </>
          ) : (
            <span className="text-muted">—</span>
          )}
          {/* Run Category PDP Analysis — same refresh icon the product and
              brand intel footers use. Disabled only while this category's
              report is generating; otherwise always available (the report
              crawls first; the API gate is the backstop). */}
          {reportRunning ? (
            <RunningBadge
              label="Analyzing"
              title="A report is running for this category — it will appear in Category Reports below when it finishes."
            />
          ) : (
            <Tooltip title="Run Category PDP Analysis — audit every product page under this category (subcategories included) and roll the findings into one report. Analyzes first only if the category has never been analyzed.">
              <button
                type="button"
                aria-label={`Run ${node.name} PDP analysis`}
                onClick={() => onRunReport(node)}
                className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
              >
                <IconRefresh size={13} />
              </button>
            </Tooltip>
          )}
        </div>
        {node.latest_report && (
          <div className="flex items-center gap-1">
            <span
              className="text-[11px] text-text-2 whitespace-nowrap"
              title={`Latest report: ${dayjs(node.latest_report.created_at).format('MMM DD, YYYY h:mm A')}`}
            >
              {dayjs(node.latest_report.created_at).format('MMM DD, YYYY')}
            </span>
            <Tooltip title="View the latest report">
              <button
                type="button"
                aria-label={`View latest ${node.name} report`}
                onClick={() => onViewReport(node)}
                className="w-6 h-6 flex items-center justify-center rounded text-[#9B958C] hover:bg-[#F4F2EE] shrink-0"
              >
                <VisibilityOutlined sx={{ fontSize: 15 }} />
              </button>
            </Tooltip>
            <Tooltip title="Download the latest report">
              <span>
                <button
                  type="button"
                  aria-label={`Download latest ${node.name} report`}
                  onClick={handleDownloadReport}
                  disabled={downloading}
                  className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                >
                  <FileDownloadOutlined sx={{ fontSize: 15 }} />
                </button>
              </span>
            </Tooltip>
          </div>
        )}
        </div>
        <span
          className="w-[80px] shrink-0 text-[12px] text-text-2"
          title={`${catalogCount} catalog products in ${node.name} and its subcategories`}
        >
          {catalogCount || <span className="text-muted">—</span>}
        </span>
        <span
          className="w-[80px] shrink-0 text-[12px] text-text-2"
          title={`${discoveredCount} found by the analysis in ${node.name} and its subcategories, not yet in your catalog`}
        >
          {discoveredCount || <span className="text-muted">—</span>}
        </span>
        <div className="w-[200px] shrink-0 flex items-center gap-1">
          {crawlable ? (
            <a
              href={node.url}
              target="_blank"
              rel="noopener noreferrer"
              title={node.url}
              className="text-[12px] text-accent-text truncate hover:underline"
            >
              {node.url.replace(/^https?:\/\/(www\.)?/, '')}
            </a>
          ) : (
            <span className="text-muted">—</span>
          )}
          <Tooltip title="Edit category name and URL">
            <button
              type="button"
              aria-label={`Edit ${node.name}`}
              onClick={() => onEditUrl(node)}
              className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
            >
              <EditOutlined sx={{ fontSize: 15 }} />
            </button>
          </Tooltip>
        </div>
        <div className="w-[110px] shrink-0 flex items-center justify-end gap-1">
          <Tooltip
            title={
              viewsDiscoveredProducts(node.catalog_product_count)
                ? "Nothing imported under this category yet — view its discovered products"
                : "View this category's products (including its subcategories)"
            }
          >
            <button
              type="button"
              aria-label={`View ${node.name} products`}
              onClick={() => onViewProducts(node)}
              className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0"
            >
              <Inventory2Outlined sx={{ fontSize: 15 }} />
            </button>
          </Tooltip>
          <Tooltip title={crawlButtonTitle(crawlable, crawlRunning, siteCrawlActive)}>
            <span>
              <button
                type="button"
                aria-label={`Analyze ${node.name}`}
                onClick={() => onCrawl(node)}
                disabled={!crawlable || crawlRunning || siteCrawlActive}
                className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              >
                <TravelExploreOutlined sx={{ fontSize: 15 }} />
              </button>
            </span>
          </Tooltip>
          <Tooltip title="Delete this category and its subcategories (products stay in your catalog)">
            <button
              type="button"
              aria-label={`Delete ${node.name}`}
              onClick={() => onDelete(node)}
              className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 hover:text-down shrink-0"
            >
              <DeleteOutline sx={{ fontSize: 15 }} />
            </button>
          </Tooltip>
        </div>
      </div>
      {hasChildren && isOpen &&
        (node.children || []).map((child) => (
          <CategoryRow
            key={child.id}
            node={child}
            expanded={expanded}
            runningIds={runningIds}
            activeCategoryIds={activeCategoryIds}
            siteCrawlActive={siteCrawlActive}
            onToggle={onToggle}
            onCrawl={onCrawl}
            onViewProducts={onViewProducts}
            onRunReport={onRunReport}
            onViewReport={onViewReport}
            onEditUrl={onEditUrl}
            onDelete={onDelete}
          />
        ))}
    </>
  )
}

// Immutable tree update: merge a partial patch into one node wherever it sits.
const patchNode = (
  nodes: RmnCategoryNode[],
  id: number,
  patch: Partial<RmnCategoryNode>,
): RmnCategoryNode[] =>
  nodes.map((n) => ({
    ...n,
    ...(n.id === id ? patch : {}),
    children: n.children ? patchNode(n.children, id, patch) : n.children,
  }))

export default function NetworkCategoriesPage() {
  const navigate = useNavigate()
  const { basePath, domain, permittedCategoryIds } = useNetworkScope()
  // A category allowlist (internal Brand > Team) makes category adds 403.
  const canAddCategory = permittedCategoryIds === null
  // "samsclub.com's analyzed category tree" — generic when the brand has no domain.
  const treeOwner = domain ? `${domain}'s` : "Your catalog's"
  const [tree, setTree] = useState<RmnCategoryNode[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [reportNode, setReportNode] = useState<RmnCategoryNode | null>(null)
  const [editingNode, setEditingNode] = useState<RmnCategoryNode | null>(null)
  const [viewingReportId, setViewingReportId] = useState<number | null>(null)
  const [addingCategory, setAddingCategory] = useState(false)
  const [reportsRefreshKey, setReportsRefreshKey] = useState(0)
  // Categories with a generating report — fed by the reports section below
  // (it polls while anything generates, so buttons re-enable on their own).
  const [runningIds, setRunningIds] = useState<Set<number>>(new Set())

  const handleReportRows = useCallback((reportRows: { status: string; params?: any }[]) => {
    setRunningIds(
      new Set(
        reportRows
          .filter((r) => r.status === 'generating')
          .map((r) => r.params && r.params.rmn_category_id)
          .filter((id): id is number => typeof id === 'number'),
      ),
    )
  }, [])

  const loadTree = useCallback(async () => {
    try {
      const res = await axiosInstant.get(rmnCategoriesEndpoint)
      const roots = (res.data || []) as RmnCategoryNode[]
      setTree(roots)
      // Roots start expanded so the tree reads as a tree, not a flat list.
      setExpanded(new Set(roots.map((r) => r.id)))
      setError(null)
    } catch {
      setError('Failed to load your categories. Please try again.')
    }
  }, [])

  useEffect(() => {
    loadTree()
  }, [loadTree])

  // Category analyses (URL adds, per-row re-analyses, admin site crawls)
  // run in the background — show them as placeholder rows and reload the
  // tree when one settles. Brand-side analyses belong to the Brands page.
  const { crawls, refresh: refreshCrawls } = useRmnCrawls({
    onFinished: (crawl) => {
      if (isBrandKindCrawl(crawl)) return
      if (crawl.status === 'completed') {
        // URL-adds auto-start a report (stats.url_import.report_id);
        // per-row re-crawls are crawl-only.
        const reportStarted = !!(crawl.url_import && crawl.url_import.report_id)
        let message = 'Analysis finished — your categories are up to date.'
        if (reportStarted) {
          message = 'Analysis finished — categories updated. The category report is generating below.'
        }
        if (crawl.stopped) {
          // A stopped crawl still completes: everything found is kept.
          message = reportStarted
            ? 'Analysis stopped — everything it found was kept, and the category report is generating below.'
            : 'Analysis stopped — everything it found was kept.'
        }
        toast.success(message)
        loadTree()
        if (crawl.kind === 'category') setReportsRefreshKey((k) => k + 1)
      } else {
        toast.error(crawlFailureMessage(crawl))
      }
    },
  })
  const activeCrawls = crawls.filter((c) => isCrawlInProgress(c) && !isBrandKindCrawl(c))
  const siteCrawlActive = activeCrawls.some(isSiteCrawl)
  const activeCategoryIds = new Set(
    activeCrawls
      .map((c) => c.scope_category_id)
      .filter((id): id is number => typeof id === 'number'),
  )

  const stopCrawl = useCallback(
    async (crawl: RmnCrawlRow) => {
      if (await requestCrawlStop(crawl)) refreshCrawls()
    },
    [refreshCrawls],
  )

  // Per-row re-crawl: scoped crawl of that category's subtree, no report.
  const startCategoryCrawl = useCallback(
    async (node: RmnCategoryNode) => {
      try {
        await axiosInstant.post(rmnCategoryCrawlEndpoint(node.id))
        toast.success(`Analysis started for ${node.name} — new pages appear here when it finishes.`)
        refreshCrawls()
      } catch (e: any) {
        const status = e?.response?.status
        const code = e?.response?.data
        if (status === 409) {
          toast.error(
            'This category is already being analyzed, or a full catalog analysis is running — try again when it finishes.',
          )
        } else if (code === 'CategoryNotCrawlable') {
          toast.error('This category doesn\'t have a URL we can analyze — edit its URL first.')
        } else if (code === 'DomainNotAllowed') {
          toast.error('This category\'s URL isn\'t on your network\'s domains.')
        } else if (status === 429) {
          toast.error('You\'ve reached the hourly limit for URL imports. Try again later.')
        } else {
          toast.error('Couldn\'t start the analysis. Please try again.')
        }
        refreshCrawls()
      }
    },
    [refreshCrawls],
  )

  const toggle = (id: number) =>
    setExpanded((cur) => {
      const next = new Set(cur)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const categoryCount = useMemo(() => {
    const walk = (nodes: RmnCategoryNode[]): number =>
      nodes.reduce((sum, n) => sum + 1 + walk(n.children || []), 0)
    return tree ? walk(tree) : 0
  }, [tree])

  const saveCategoryEdit = useCallback(
    async (url: string, name?: string) => {
      if (!editingNode) return
      try {
        const body: { url: string; name?: string } = { url }
        if (name !== undefined) body.name = name
        const res = await axiosInstant.patch(rmnCategoryEndpoint(editingNode.id), body)
        const savedUrl = (res.data && res.data.url) || url
        const savedName = (res.data && res.data.name) || name || editingNode.name
        setTree((cur) =>
          cur ? patchNode(cur, editingNode.id, { url: savedUrl, name: savedName }) : cur,
        )
        toast.success(`Updated ${savedName}`)
      } catch (e: any) {
        const code = e?.response?.data
        if (e?.response?.status === 409 || code === 'CategoryUrlAlreadyExists') {
          throw new Error('Another category already uses this URL.')
        }
        if (code === 'NameRequired') {
          throw new Error('Enter a name.')
        }
        if (e?.response?.status === 400) {
          throw new Error('Enter a full URL starting with http:// or https://')
        }
        throw new Error("Couldn't save the changes. Please try again.")
      }
    },
    [editingNode],
  )

  const [deletingNode, setDeletingNode] = useState<RmnCategoryNode | null>(null)
  const [deleting, setDeleting] = useState(false)
  const confirmDelete = useCallback(async () => {
    if (!deletingNode) return
    setDeleting(true)
    try {
      await axiosInstant.delete(rmnCategoryEndpoint(deletingNode.id))
      toast.success(`Deleted ${deletingNode.name}`)
      setDeletingNode(null)
      // Reload — subtree removal shifts counts and scores up the tree.
      loadTree()
    } catch (e: any) {
      if (e?.response?.status === 404) {
        toast.error('This category was already removed.')
        setDeletingNode(null)
        loadTree()
      } else {
        toast.error("Couldn't delete the category. Please try again.")
      }
    } finally {
      setDeleting(false)
    }
  }, [deletingNode, loadTree])

  return (
    <MainContainer>
      <ListPageHeader
        title="Categories"
        subtitle={
          categoryCount > 0
            ? `${treeOwner} analyzed category tree · ${categoryCount} categories`
            : `${treeOwner} analyzed category tree`
        }
        actionLabel={canAddCategory ? 'Add category' : undefined}
        onActionClick={canAddCategory ? () => setAddingCategory(true) : undefined}
        actionIcon={<IconPlus width={16} height={16} stroke="#fff" />}
        actionDisabled={siteCrawlActive}
        actionTitle={siteCrawlActive ? SITE_CRAWL_MESSAGE : undefined}
      />

      {error && <div className="text-[14px] text-down py-6">{error}</div>}

      {!error && tree === null && (
        <div className="flex justify-center py-16">
          <DotLottleLoader />
        </div>
      )}

      {!error && tree !== null && tree.length === 0 && activeCrawls.length === 0 && (
        <div className="max-w-[560px] text-[14px] text-text-2 leading-relaxed py-10">
          <p className="font-semibold text-text-1 mb-2">No categories yet</p>
          <p>
            Add a category by URL above, or contact your Desire Company account manager to
            set up a full analysis for your brand.
          </p>
        </div>
      )}

      {!error && tree !== null && (tree.length > 0 || activeCrawls.length > 0) && (
        <div className="overflow-x-auto">
          <div className="min-w-[910px] text-[13px]">
            {/* Header strip mirrors the DataTable TH styling so Categories
                reads like the Brands and Products tables. */}
            <div className="flex items-center gap-2 px-3 py-2.5 text-[10.5px] font-semibold text-text-2 uppercase tracking-[0.5px] border-b border-border-token bg-surface-2">
              <span className="w-6 shrink-0" />
              <span className="flex-1">Category</span>
              <span className="w-[190px] shrink-0">PDP Score</span>
              <span className="w-[80px] shrink-0">Catalog</span>
              <span className="w-[80px] shrink-0">Discovered</span>
              <span className="w-[200px] shrink-0">Category URL</span>
              <span className="w-[110px] shrink-0" />
            </div>
            {/* In-flight crawls, aligned with the tree's columns: status in
                the Score slot, live page count in the Catalog slot. */}
            {activeCrawls.map((crawl) => (
              <div
                key={`crawl-${crawl.id}`}
                className="flex items-center gap-2 border-b border-border-token px-3 py-2.5 bg-surface-2"
              >
                <span className="w-6 shrink-0 flex items-center justify-center">
                  <CircularProgress size={14} sx={{ color: 'var(--accent-text)' }} />
                </span>
                <span className="flex-1 min-w-0 text-[13px] text-text-2 truncate">
                  {crawlRowLabel(crawl)}…
                </span>
                <span className="w-[190px] shrink-0">
                  <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-accent-text bg-tint rounded-full px-2.5 py-1">
                    {crawlStatusLabel(crawl)}
                  </span>
                </span>
                <span className="w-[80px] shrink-0 text-[12px] text-muted">
                  {crawl.pages_fetched > 0 ? `${crawl.pages_fetched} pages` : '—'}
                </span>
                <span className="w-[80px] shrink-0" />
                <span className="w-[200px] shrink-0" />
                <span className="w-[110px] shrink-0 flex items-center justify-end">
                  {isCrawlStoppable(crawl) && (
                    <button
                      type="button"
                      title="Stop this analysis — everything it already found will still be processed."
                      onClick={() => stopCrawl(crawl)}
                      className="text-[12px] font-semibold text-text-2 border border-border-token rounded-md px-2.5 py-1.5 bg-transparent cursor-pointer hover:bg-surface-2"
                    >
                      Stop
                    </button>
                  )}
                </span>
              </div>
            ))}
            {tree.map((node) => (
              <CategoryRow
                key={node.id}
                node={node}
                expanded={expanded}
                runningIds={runningIds}
                activeCategoryIds={activeCategoryIds}
                siteCrawlActive={siteCrawlActive}
                onToggle={toggle}
                onCrawl={startCategoryCrawl}
                // The Products list's category filter matches the whole
                // subtree, so this shows the category's products including
                // its subcategories'. Nothing imported under it yet → the
                // Discovered tab, where its crawled rows are waiting.
                onViewProducts={(n) =>
                  navigate(
                    viewsDiscoveredProducts(n.catalog_product_count)
                      ? `${basePath}/products/discovered?category=${n.id}`
                      : `${basePath}/products?rmn_category_id=${n.id}`,
                  )
                }
                onRunReport={setReportNode}
                onViewReport={(n) => n.latest_report && setViewingReportId(n.latest_report.id)}
                onEditUrl={setEditingNode}
                onDelete={setDeletingNode}
              />
            ))}
          </div>
        </div>
      )}

      {!error && tree !== null && tree.length > 0 && (
        <div className="mt-10">
          <ReportHistorySection
            reportType="category"
            title="Category Report History"
            refreshKey={reportsRefreshKey}
            onRowsChange={handleReportRows}
          />
        </div>
      )}

      <Dialog open={!!reportNode} onClose={() => setReportNode(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Category PDP Analysis
          <IconButton aria-label="Close" onClick={() => setReportNode(null)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {reportNode && (
            <CategoryAuditForm
              defaultCategoryId={reportNode.id}
              onAnalyze={(node) => {
                startCategoryCrawl(node)
                setReportNode(null)
              }}
              analyzeDisabledReason={(node) => {
                if (!isDisplayableUrl(node.url)) return 'Set a category URL we can analyze first (edit the URL).'
                if (activeCategoryIds.has(node.id) || siteCrawlActive) {
                  return 'An analysis is already running — try again when it finishes.'
                }
                return null
              }}
              onCreated={() => {
                setReportNode(null)
                // The report lands in this page's own list with a
                // Generating badge that polls until ready.
                setReportsRefreshKey((k) => k + 1)
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <EditUrlDialog
        open={!!editingNode}
        title={editingNode ? `Edit category — ${editingNode.name}` : 'Edit category'}
        showNameField
        nameLabel="Category name"
        initialName={editingNode ? editingNode.name : ''}
        label="Category URL"
        helperText="Where this category lives on the retailer site."
        initialUrl={editingNode && isDisplayableUrl(editingNode.url) ? editingNode.url : ''}
        onClose={() => setEditingNode(null)}
        onSave={saveCategoryEdit}
      />

      <ConfirmationDialog
        open={!!deletingNode}
        onClose={() => setDeletingNode(null)}
        onConfirm={confirmDelete}
        disabled={deleting}
        title={deletingNode ? `Delete ${deletingNode.name}?` : 'Delete category?'}
        description={[
          'This removes the category and all of its subcategories from your catalog. It stays deleted on future analyses — you can re-add it later with Add category.',
          'Products are not deleted — they stay in your catalog.',
        ]}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
      />

      <EditUrlDialog
        open={addingCategory}
        title="Add category"
        label="Category URL"
        helperText="Paste the category's page on the retailer site — we'll analyze it and add the category with its subcategories, products and brands. This runs in the background and takes a few minutes."
        initialUrl=""
        allowClear={false}
        saveLabel="Start analysis"
        savingLabel="Starting…"
        onClose={() => setAddingCategory(false)}
        onSave={async (url) => {
          try {
            await axiosInstant.post(rmnImportCategoryUrlEndpoint, { url })
            toast.success(
              'Analysis started — the category and its products will appear here in a few minutes.',
            )
            // Light the placeholder row right away.
            refreshCrawls()
          } catch (e: any) {
            const status = e?.response?.status
            const code = e?.response?.data
            if (status === 409) {
              throw new Error(
                'That URL is already being analyzed, or a full catalog analysis is running — try again when it finishes.',
              )
            }
            if (code === 'DomainNotAllowed') {
              throw new Error('That URL isn\'t on your network\'s domains.')
            }
            if (status === 429) {
              throw new Error('You\'ve reached the hourly limit for URL imports. Try again later.')
            }
            throw new Error('Couldn\'t start the analysis. Please try again.')
          }
        }}
      />
      {viewingReportId !== null && (
        <ReportViewerDialog reportId={viewingReportId} onClose={() => setViewingReportId(null)} />
      )}
    </MainContainer>
  )
}
